import React, { useMemo } from "react";
import { Star, GitFork, Eye } from "lucide-react";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { sanitizeRepo } from "components/utils";
import { useGithubRepositoryStatsQuery } from "server/github/queries";

interface GitHubRepoRendererProps {
  repoUrl: string;
}

const GitHubRepoRenderer: React.FC<GitHubRepoRendererProps> = ({ repoUrl }) => {
  const {
    data: info,
    isError,
    isLoading,
  } = useGithubRepositoryStatsQuery(repoUrl);
  const { ownerName, repoName } = useMemo(() => {
    const parts = sanitizeRepo(repoUrl).replace(/\/+$/, "").split("/");
    return {
      ownerName: parts[parts.length - 2] ?? "",
      repoName: (parts[parts.length - 1] ?? "").replace(/\s/g, "-"),
    };
  }, [repoUrl]);

  const repoLink = sanitizeRepo(repoUrl).startsWith("http")
    ? sanitizeRepo(repoUrl)
    : `https://${sanitizeRepo(repoUrl)}`;

  return (
    <div className="bg-primaryBg border border-primaryBorder rounded-lg p-6 mb-4 text-white">
      <div className="flex items-center gap-3 mb-4">
        <GitHubLogoIcon className="w-8 h-8 text-blue-400" />
        <a
          href={repoLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xl font-bold hover:underline"
        >
          {sanitizeRepo(repoName)}
        </a>
        <span className="text-gray-400 text-lg">by {ownerName}</span>
      </div>
      {isLoading ? (
        <p role="status" className="text-gray-300">
          Loading repository stats...
        </p>
      ) : isError || !info ? (
        <p role="status" className="text-gray-300">
          Repository stats unavailable.
        </p>
      ) : (
        <div className="flex gap-6 text-gray-300">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            <span>{info.stars} stars</span>
          </div>
          <div className="flex items-center gap-2">
            <GitFork className="w-5 h-5 text-green-400" />
            <span>{info.forks} forks</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-400" />
            <span>{info.watchers} watchers</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubRepoRenderer;
